import React, {
  useState, useCallback, useEffect, useRef, useMemo,
} from 'react'
import { connect } from 'react-redux'
import { SearchContext } from '../../services/search/SearchContext'
import { SupplementarySearchContext } from '../../services/search/SupplementarySearchContext'
import { reportLocalVsAlgolia } from '../../services/search/searchReporter'
import AlgoliaSearchProvider from '../../services/search/providers/AlgoliaSearchProvider'
import FuseContentSearchProvider from '../../services/search/providers/FuseContentSearchProvider'
import MiniSearchContentProvider from '../../services/search/providers/MiniSearchContentProvider'
import NavigationSearchProvider from '../../services/search/providers/NavigationSearchProvider'
import NavigationMiniSearchProvider from '../../services/search/providers/NavigationMiniSearchProvider'
import ExternalLinksSearchProvider from '../../services/search/providers/ExternalLinksSearchProvider'
import ExternalLinksMiniSearchProvider from '../../services/search/providers/ExternalLinksMiniSearchProvider'
import QuickActionsSearchProvider from '../../services/search/providers/QuickActionsSearchProvider'
import QuickActionsMiniSearchProvider from '../../services/search/providers/QuickActionsMiniSearchProvider'

// ─── Provider role config ──────────────────────────────────────────────────────
// Extend this list to add/remove providers without changing any consumer code.
// role: 'display'  → results populate SearchContext (shown to the user)
// role: 'shadow'   → results sent to searchReporter only (quality assessment)
// role: 'additive' → results populate SupplementarySearchContext (supplementary UI slots)

// ─── Local search config ───────────────────────────────────────────────────────
// Content search is displayed from Algolia only. Fuse + MiniSearch content
// providers run as shadows (quality assessment via searchReporter).
//
// For non-content registries (navigation / external-help / quick-actions) both
// local engines always run and land in SupplementarySearchContext. PRESENTATION_ENGINE
// selects which engine's results the UI presents for those slots (via
// localProviderId); the other engine still runs to feed the non-content section
// of the search-quality report.
const PRESENTATION_ENGINE = 'fuse' // 'fuse' | 'minisearch'

// When true, the settled result set is compared (Algolia vs both local
// engines) and the JSON quality report is emitted (analytics + dev console).
const REPORTING_ENABLED = true

// Each provider (Algolia display + the Fuse/MiniSearch content and non-content
// providers) reports its results independently and asynchronously, so the
// result state updates several times as a single query settles. Debounce the
// quality report by this many ms after the LAST update so exactly one report
// is emitted per query (one CDP record per search, not one per provider).
const REPORT_DEBOUNCE_MS = 600

function buildProviderConfig(algoliaSearchKey) {
  const config = []
  if (algoliaSearchKey) {
    config.push({
      id: 'algolia', role: 'display', component: AlgoliaSearchProvider,
    })
  }
  // Local content search — both engines run as shadows (not shown to the user).
  config.push({
    id: 'fuse-content', role: 'shadow', label: 'Library (Fuse)', component: FuseContentSearchProvider,
  })
  config.push({
    id: 'minisearch-content', role: 'shadow', label: 'Library (BM25)', component: MiniSearchContentProvider,
  })
  // Navigation registry — both engines always run.
  config.push({
    id: 'navigation', role: 'additive', label: 'Navigation', component: NavigationSearchProvider,
  })
  config.push({
    id: 'navigation-minisearch', role: 'additive', label: 'Navigation (BM25)', component: NavigationMiniSearchProvider,
  })
  // External / help links registry — both engines always run.
  config.push({
    id: 'external-help', role: 'additive', label: 'Help & External', component: ExternalLinksSearchProvider,
  })
  config.push({
    id: 'external-help-minisearch', role: 'additive', label: 'Help & External (BM25)', component: ExternalLinksMiniSearchProvider,
  })
  // Quick actions registry — both engines always run.
  config.push({
    id: 'quick-actions', role: 'additive', label: 'Quick Actions', component: QuickActionsSearchProvider,
  })
  config.push({
    id: 'quick-actions-minisearch', role: 'additive', label: 'Quick Actions (BM25)', component: QuickActionsMiniSearchProvider,
  })
  return config
}

// ─── Options builder ───────────────────────────────────────────────────────────

const TOP_NAV_HITS_PER_PAGE = 4
const SECTION_HOME_HITS_PER_PAGE = 200
const CONTENT_SEARCH_FILTERS = 'archived!=1 AND parentArchived!=1'

function buildAlgoliaOptions(isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey) {
  const primaryIndex = isSectionHome ? 'sets_questions' : 'repos'
  // Secondary indexes are queried in parallel for multi-index search.
  // isSectionHome queries only the primary index; all other contexts include
  // both folders and sets_questions alongside repos.
  const secondaryIndexes = isSectionHome ? [] : ['folders', 'sets_questions']
  let resolvedHitsPerPage = hitsPerPage
  if (isTopNav) resolvedHitsPerPage = TOP_NAV_HITS_PER_PAGE
  else if (isSectionHome) resolvedHitsPerPage = SECTION_HOME_HITS_PER_PAGE
  return {
    apiKey: algoliaSearchKey,
    primaryIndex,
    secondaryIndexes,
    hitsPerPage: resolvedHitsPerPage,
    filters: CONTENT_SEARCH_FILTERS,
  }
}

// ─── Default context value ─────────────────────────────────────────────────────

const DEFAULT_INDEX_RESULT = {
  hits: [], hitCount: 0, hasMore: false, loadMore: () => {},
}

const DEFAULT_RESULTS = {
  suggestions: [],
  hits: [],
  indexResults: {
    repos: { ...DEFAULT_INDEX_RESULT },
    folders: { ...DEFAULT_INDEX_RESULT },
    // eslint-disable-next-line camelcase
    sets_questions: { ...DEFAULT_INDEX_RESULT },
  },
}

// ─── SearchOrchestrator ────────────────────────────────────────────────────────

/**
 * Owns query state and renders all search providers as headless siblings.
 * Routes each provider's results to the correct context based on its role.
 *
 * Consumer components (TopNavSearchBar, SearchResultsContainer, etc.) read only
 * from SearchContext or SupplementarySearchContext — they have no knowledge of
 * which providers are active.
 */
function SearchOrchestrator({
  algoliaSearchKey,
  isTopNav,
  isSectionHome,
  hitsPerPage,
  searchState,
  onSearchStateChange,
  children,
}) {
  const [query, setQuery] = useState(() => (searchState && searchState.query) || '')
  const [displayResults, setDisplayResults] = useState(DEFAULT_RESULTS)
  const [additiveResults, setAdditiveResults] = useState([])
  const [shadowResults, setShadowResults] = useState([])

  // Sync query when the URL-driven searchState prop changes (e.g. browser back).
  // React bails out of a re-render when setQuery is called with the same value,
  // so there is no risk of an infinite loop with the onSearchStateChange effect.
  useEffect(() => {
    const externalQuery = (searchState && searchState.query) || ''
    setQuery(externalQuery)
  }, [searchState])

  // Notify the parent (e.g. SearchResultsContainer) when the query changes so
  // it can debounce URL updates. Use a ref so onSearchStateChange can change
  // without this effect becoming stale.
  const onSearchStateChangeRef = useRef(onSearchStateChange)
  onSearchStateChangeRef.current = onSearchStateChange
  useEffect(() => {
    if (onSearchStateChangeRef.current) {
      onSearchStateChangeRef.current({ query })
    }
  }, [query])

  // Route results based on the provider's role.
  //
  // Additive blocks carry both the flat `hits` list and the optional
  // `indexResults` object. Providers that mirror Algolia's per-index shape
  // (currently FuseContentSearchProvider) populate `indexResults`; lightweight
  // additive providers (navigation, external-help) leave it undefined and
  // consumers fall back to reading flat `hits`.
  const handleResults = useCallback((role, providerId, results) => {
    if (role === 'display') {
      setDisplayResults(results)
    } else if (role === 'additive') {
      setAdditiveResults((prev) => {
        const without = prev.filter((r) => r.providerId !== providerId)
        return [...without, {
          providerId,
          hits: results.hits || [],
          indexResults: results.indexResults,
        }]
      })
    } else if (role === 'shadow') {
      setShadowResults((prev) => {
        const without = prev.filter((r) => r.providerId !== providerId)
        return [...without, {
          providerId,
          hits: results.hits || [],
          indexResults: results.indexResults,
        }]
      })
    }
  }, [])

  // Stable references — providers receive the same options object identity
  // until their inputs actually change, preventing cascading re-renders that
  // would re-trigger Algolia's connector HOCs.
  const algoliaOptions = useMemo(
    () => buildAlgoliaOptions(isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey),
    [isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey],
  )
  const providerConfig = useMemo(
    () => buildProviderConfig(algoliaSearchKey),
    [algoliaSearchKey],
  )
  const displayProviderId = useMemo(
    () => (providerConfig.find((p) => p.role === 'display') || {}).id || 'unknown',
    [providerConfig],
  )

  // Memoised per-provider onResults callback. Identity is stable for the
  // lifetime of the orchestrator since handleResults is stable.
  const onResultsForProvider = useCallback(
    (role, id) => (results) => handleResults(role, id, results),
    [handleResults],
  )
  const onResultsCacheRef = useRef(new Map())
  const getOnResults = (role, id) => {
    const key = `${role}:${id}`
    if (!onResultsCacheRef.current.has(key)) {
      onResultsCacheRef.current.set(key, onResultsForProvider(role, id))
    }
    return onResultsCacheRef.current.get(key)
  }

  const searchContextValue = useMemo(
    () => ({
      ...displayResults,
      currentRefinement: query,
      refine: setQuery,
    }),
    [displayResults, query],
  )

  const supplementaryContextValue = useMemo(
    () => ({ additiveResults, presentationEngine: PRESENTATION_ENGINE }),
    [additiveResults],
  )

  // Search-quality report — comparing Algolia against both local engines.
  // Gated on REPORTING_ENABLED. The providers settle their results in a burst
  // (each fires independently), so this effect re-runs several times per query;
  // we schedule the report on a trailing debounce and the cleanup cancels any
  // still-pending report from an earlier update. Net effect: exactly one report
  // per query, built from the fully-settled result state.
  useEffect(() => {
    if (!REPORTING_ENABLED) return undefined
    if (!query) return undefined
    const handle = setTimeout(() => {
      reportLocalVsAlgolia({
        query,
        displayedProvider: displayProviderId,
        displayedResults: displayResults,
        additiveResults,
        shadowResults,
      })
    }, REPORT_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [
    query,
    displayProviderId,
    displayResults,
    additiveResults,
    shadowResults,
  ])

  if (algoliaSearchKey) {
    return (
      <SearchContext.Provider value={searchContextValue}>
        <SupplementarySearchContext.Provider value={supplementaryContextValue}>
          {providerConfig.map(({ id, role, component: Provider }) => (
            <Provider
              key={id}
              query={query}
              options={algoliaOptions}
              onResults={getOnResults(role, id)}
              displayedResults={displayResults}
              displayedProvider={displayProviderId}
            />
          ))}
          {children}
        </SupplementarySearchContext.Provider>
      </SearchContext.Provider>
    )
  }
  if (isTopNav) {
    return <input disabled className='topNav-search' />
  }
  return null
}

function mapStateToProps(state) {
  const { searchKey } = state
  let contentsSearchKey
  if (searchKey) {
    contentsSearchKey = searchKey.contents
  }
  return {
    algoliaSearchKey: contentsSearchKey,
  }
}

export default connect(
  mapStateToProps,
  {},
)(SearchOrchestrator)
